/**
 * Weekly breaks: recurring blocks such as lunch, stored on a member
 * schedule's `Schedule.hours` day entry as `breaks: [{ start, end }]`
 * (location-local "HH:mm", exclusive end). They apply on every open day of
 * that weekday, including days whose hours an `ExtraHour` overrides.
 *
 * Shared by the booking engine (`apps/api/.../appointments/openings.ts`), the
 * availability mirror (`memberDayAvailability.ts`), and the dashboard and
 * staff-portal write paths, so every surface reads and writes them the same
 * way.
 */
import {
  MIN_TIME_BLOCK_MINUTES,
  minutesToDbTime,
  parseDbTimeToMinutes,
} from "./time";

export interface WeeklyBreak {
  start: string;
  end: string;
}

/** The open window a day's breaks must fit in. */
export interface WeeklyBreakDay {
  isOpen: boolean;
  open: string | null;
  close: string | null;
}

export const MAX_WEEKLY_BREAKS_PER_DAY = 3;

const MINUTES_PER_DAY = 24 * 60;

/** Minutes of a canonical "HH:mm" (or "24:00") string, else null. */
function canonicalMinutes(value: unknown): number | null {
  const minutes = parseDbTimeToMinutes(value);
  if (minutes == null) return null;
  const canonical =
    minutes === MINUTES_PER_DAY ? "24:00" : minutesToDbTime(minutes);
  return canonical === value ? minutes : null;
}

function dayWindow(day: WeeklyBreakDay): { open: number; close: number } | null {
  if (!day.isOpen) return null;
  const open = parseDbTimeToMinutes(day.open);
  const close = parseDbTimeToMinutes(day.close);
  if (open == null || close == null || close <= open) return null;
  return { open, close };
}

/**
 * Any stored or submitted value → the well-formed breaks in it, sorted by
 * start. Read-side and lenient: entries that are not canonical "HH:mm" pairs
 * with `start < end` are dropped, never thrown on.
 */
export function normalizeWeeklyBreaks(raw: unknown): WeeklyBreak[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: Array<WeeklyBreak & { startMinutes: number }> = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const { start, end } = entry as { start?: unknown; end?: unknown };
    const startMinutes = canonicalMinutes(start);
    const endMinutes = canonicalMinutes(end);
    if (startMinutes == null || endMinutes == null) continue;
    if (endMinutes <= startMinutes) continue;
    const key = `${start}-${end}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ start: start as string, end: end as string, startMinutes });
  }
  return out
    .sort((a, b) => a.startMinutes - b.startMinutes)
    .map(({ start, end }) => ({ start, end }));
}

/**
 * Write-side check of one day's breaks. Returns the first problem as a
 * user-facing sentence, or null when they are valid. A closed day's breaks
 * are not checked: `mergeIncomingBreaks` drops them.
 */
export function validateDayBreaks(
  day: WeeklyBreakDay,
  breaks: readonly WeeklyBreak[],
): string | null {
  if (!day.isOpen || breaks.length === 0) return null;
  if (breaks.length > MAX_WEEKLY_BREAKS_PER_DAY) {
    return `A day can have at most ${MAX_WEEKLY_BREAKS_PER_DAY} breaks.`;
  }

  const window = dayWindow(day);
  const ranges: Array<{ start: number; end: number }> = [];
  for (const entry of breaks) {
    const start = canonicalMinutes(entry.start);
    const end = canonicalMinutes(entry.end);
    if (
      start == null ||
      end == null ||
      start % MIN_TIME_BLOCK_MINUTES !== 0 ||
      end % MIN_TIME_BLOCK_MINUTES !== 0
    ) {
      return "Break times must be on a 5-minute grid (HH:mm).";
    }
    if (end <= start) return "A break must end after it starts.";
    if (!window || start < window.open || end > window.close) {
      return "Breaks must fall within the day's hours.";
    }
    ranges.push({ start, end });
  }

  ranges.sort((a, b) => a.start - b.start);
  for (let index = 1; index < ranges.length; index += 1) {
    if (ranges[index].start < ranges[index - 1].end) {
      return "Breaks on the same day cannot overlap.";
    }
  }
  return null;
}

/**
 * The breaks to store for a day being saved.
 *
 * - closed day → none
 * - no `breaks` key on the incoming day → keep the stored breaks that still
 *   fit the new hours. Old dashboard tabs and staff-app pages that predate
 *   breaks send no key, and zod would otherwise strip the field and erase them.
 * - `breaks: []` → clear
 * - `breaks: [...]` → those (validate them with `validateDayBreaks` first)
 */
export function mergeIncomingBreaks(
  incoming: WeeklyBreakDay & { breaks?: unknown },
  stored: unknown,
): WeeklyBreak[] {
  const window = dayWindow(incoming);
  if (!window) return [];
  if (incoming.breaks === undefined) {
    return normalizeWeeklyBreaks(stored).filter((entry) => {
      const start = parseDbTimeToMinutes(entry.start)!;
      const end = parseDbTimeToMinutes(entry.end)!;
      return start >= window.open && end <= window.close;
    });
  }
  return normalizeWeeklyBreaks(incoming.breaks);
}

/** Each open day's first break problem, keyed by weekday (editors). */
export function weeklyBreakErrors(
  days: ReadonlyArray<
    WeeklyBreakDay & { day: number; breaks?: readonly WeeklyBreak[] }
  >,
): Record<number, string> {
  const errors: Record<number, string> = {};
  for (const day of days) {
    const message = validateDayBreaks(day, day.breaks ?? []);
    if (message) errors[day.day] = message;
  }
  return errors;
}

function formatBoundary(minutes: number): string {
  return minutes === MINUTES_PER_DAY ? "24:00" : minutesToDbTime(minutes);
}

/**
 * The break an editor's "Add break" inserts: lunch at noon when it fits,
 * else the first free whole hour, else the first free 30 or 15 minutes.
 * Null when the day is closed, already has the maximum, or has no room.
 */
export function suggestWeeklyBreak(
  day: WeeklyBreakDay,
  existing: readonly WeeklyBreak[],
): WeeklyBreak | null {
  const window = dayWindow(day);
  if (!window || existing.length >= MAX_WEEKLY_BREAKS_PER_DAY) return null;

  const taken = normalizeWeeklyBreaks(existing).map((entry) => ({
    start: parseDbTimeToMinutes(entry.start)!,
    end: parseDbTimeToMinutes(entry.end)!,
  }));
  const fits = (start: number, end: number) =>
    start >= window.open &&
    end <= window.close &&
    taken.every((range) => end <= range.start || start >= range.end);
  const toBreak = (start: number, end: number): WeeklyBreak => ({
    start: formatBoundary(start),
    end: formatBoundary(end),
  });

  if (fits(12 * 60, 13 * 60)) return toBreak(12 * 60, 13 * 60);
  for (const length of [60, 30, 15]) {
    const step = length === 60 ? 60 : 15;
    for (
      let start = Math.ceil(window.open / step) * step;
      start + length <= window.close;
      start += step
    ) {
      if (fits(start, start + length)) return toBreak(start, start + length);
    }
  }
  return null;
}

/**
 * The slots of a day's grid that its weekly breaks block: every 5-minute slot
 * `[t, t + 5)` that overlaps a break. Matched by interval, not by slot string,
 * because the grid is anchored to the day's opening time (hours opening at
 * 09:02 put slots at :02 and :07) while breaks sit on the wall clock. An
 * off-grid slot that only touches a break is blocked too, so no booking can
 * run into it.
 */
export function weeklyBreakBlockedSlots(
  breaks: readonly WeeklyBreak[] | null | undefined,
  slots: readonly string[],
): string[] {
  if (!breaks?.length || slots.length === 0) return [];
  const ranges = normalizeWeeklyBreaks(breaks).map((entry) => ({
    start: parseDbTimeToMinutes(entry.start)!,
    end: parseDbTimeToMinutes(entry.end)!,
  }));
  return slots.filter((slot) => {
    const start = parseDbTimeToMinutes(slot);
    if (start == null) return false;
    const end = start + MIN_TIME_BLOCK_MINUTES;
    return ranges.some((range) => start < range.end && end > range.start);
  });
}
